import type { SeatOption, SeatSelection as SeatSelectionRequest } from '@gatecheckpro/contracts';

/**
 * The most chairs one hold may ask for.
 *
 * The authority is `seatSelectionSchema`, which refuses more; this is the same
 * number said where a control can read it, and a test parses a selection one
 * over the limit so the two cannot drift apart quietly.
 */
export const MOST_SEATS_PER_HOLD = 50;

/**
 * What a customer has pointed at, and nothing more than that.
 *
 * The single most dangerous idea available to this file is that picking a
 * chair reserves it. It does not. Nothing here holds a seat, and nothing here
 * decides whether a seat can be held: this is a record of an intention, which
 * the server is then asked to grant or refuse. Two people can hold this exact
 * selection at the same time, and exactly one of them will get the chair.
 *
 * The shape is the request shape on purpose. A selection that had to be
 * translated into something else on the way out would be a second place where
 * a table could quietly become a list of chairs.
 */
export type SeatSelection = { readonly kind: 'NONE' } | SeatSelectionRequest;

export const NOTHING_SELECTED: SeatSelection = { kind: 'NONE' };

/**
 * Why a chair cannot be chosen, in the customer's terms.
 *
 * `null` means it can. These are display reasons for a control that would
 * otherwise be inert with no explanation — they are not an authority, and a
 * chair that passes every one of them can still be refused by the server a
 * moment later, because somebody else was quicker.
 */
export type UnchoosableReason = 'NOT_ON_SALE' | 'BEING_BOUGHT' | 'SOLD' | 'SOLD_WITH_ITS_TABLE';

export function whyNotChoosable(seat: SeatOption): UnchoosableReason | null {
  // The server says which chairs belong to a table sold whole. A page that
  // offered one of them individually would be setting somebody up for a
  // refusal it could have avoided saying.
  if (seat.soldWithTableId !== null) return 'SOLD_WITH_ITS_TABLE';
  if (seat.saleDisposition !== 'SELLABLE') return 'NOT_ON_SALE';
  if (seat.availability === 'HELD') return 'BEING_BOUGHT';
  if (seat.availability === 'SOLD') return 'SOLD';
  return seat.availability === 'AVAILABLE' ? null : 'NOT_ON_SALE';
}

/**
 * Adds or removes one chair.
 *
 * Choosing a chair abandons a table: a table sold whole and a chair sold on
 * its own are different commercial acts, and a selection holding both would be
 * asking the server for something it must refuse. Better to make the swap
 * visible in the room than to send an incoherent request and explain a refusal.
 */
export function toggleSeat(selection: SeatSelection, venueMapSeatId: string): SeatSelection {
  const chosen = selection.kind === 'SEATS' ? selection.venueMapSeatIds : [];
  const without = chosen.filter((id) => id !== venueMapSeatId);
  // At the limit, adding is refused here rather than by the server. A person
  // who has already chosen fifty chairs is better told so than shown a refusal
  // for the whole selection after the fifty-first click.
  if (without.length === chosen.length && chosen.length >= MOST_SEATS_PER_HOLD) return selection;
  const next = without.length === chosen.length ? [...chosen, venueMapSeatId] : without;
  // Sorted, so the same set of chairs is always the same request. An order
  // that followed clicks would make two identical selections look different to
  // anything comparing or caching them.
  return next.length === 0
    ? NOTHING_SELECTED
    : { kind: 'SEATS', venueMapSeatIds: [...next].sort() };
}

/**
 * Asks for the table, not for the chairs around it.
 *
 * The server expands a table into its constituent seats and claims them
 * atomically. A browser that sent the chairs instead would have made itself
 * responsible for knowing which chairs belong to which table and for what
 * whole-table mode means — and it would be wrong the moment a table's seats
 * changed under it.
 */
export function selectWholeTable(venueMapTableId: string): SeatSelection {
  return { kind: 'WHOLE_TABLE', venueMapTableId };
}

export function isSeatSelected(selection: SeatSelection, venueMapSeatId: string): boolean {
  return selection.kind === 'SEATS' && selection.venueMapSeatIds.includes(venueMapSeatId);
}

export function selectedSeatCount(selection: SeatSelection): number {
  return selection.kind === 'SEATS' ? selection.venueMapSeatIds.length : 0;
}

/** True once there is something a hold could be asked for. */
export function isRequestable(selection: SeatSelection): boolean {
  return selection.kind === 'WHOLE_TABLE' || selectedSeatCount(selection) > 0;
}

/**
 * The selection as the acquisition request carries it, or null when there is
 * nothing to ask for.
 *
 * Deliberately no product, no frozen version and no allocation. The customer
 * picked chairs; what those chairs are sold as is resolved by the server from
 * the configuration that is actually selling, and a browser that proposed
 * those identifiers would be proposing terms.
 */
export function acquisitionSelection(selection: SeatSelection): SeatSelectionRequest | null {
  return selection.kind === 'NONE' ? null : selection;
}
